/**
 * tRPC v11 client wired to TanStack Query (doc 04 · Topology: browser -> HTTP tRPC -> api).
 *
 * `@trpc/tanstack-react-query` exposes the "options proxy" pattern: `createTRPCContext` gives a
 * `TRPCProvider` plus a `useTRPC()` hook returning a proxy whose leaves build TanStack Query
 * option objects (`trpc.system.ping.queryOptions()`), which are then passed to `useQuery` /
 * `useMutation` from `@tanstack/react-query` directly. There is no `trpc.useQuery` in v11.
 *
 * The api registers tRPC without a data transformer, so the client must not configure one either.
 */
import type { AppRouter } from '@elizmonday/api';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, httpLink, splitLink, TRPCClientError } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { reportError } from '../app/errors';

/** Same-origin in production (the api serves the SPA) and via the Vite dev proxy locally. */
export const TRPC_ENDPOINT = '/trpc';

/**
 * The CSRF header of M0-F3 §5.3. A cross-origin form post cannot set it without a CORS
 * preflight, so its presence — together with a trusted `Origin` — is what tells the api that
 * a cookie-authenticated mutation really came from this SPA.
 */
export const CLIENT_HEADER = 'x-elizmonday-client';
export const CLIENT_HEADER_VALUE = 'web';

/** The routes that are reachable without a session; a 401 there must not bounce (§8.8). */
const PUBLIC_PATHS = [
  /^\/login/,
  /**
   * P-038. `/logout` calls `auth.logout` and then leaves for `/login` itself. A 401 there —
   * the session had already expired — must not bounce through `/login?next=/logout` and run
   * the whole thing again.
   */
  /^\/logout/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/invite/,
  /^\/join/,
  // The `/__ui` fixtures run with no session on purpose (M0-F7 §10, parity card P-002).
  /^\/__ui/,
];

/**
 * §8.8: a 401 from any tRPC call means the session is gone. The SPA sends the person to
 * `/login` with the current route preserved in `?next=`, which is the same thing the modal
 * described in the spec does, without owning a piece of the shell another module renders.
 */
export function handleUnauthenticated(pathname: string, search: string): void {
  if (PUBLIC_PATHS.some((pattern) => pattern.test(pathname))) return;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`/login?next=${next}`);
}

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export function createQueryClient(): QueryClient {
  return new QueryClient({
    /*
     * P-011. Every mutation failure in the product passes through here, whatever the call site
     * does about it. It has to be the **cache** callback and not `defaultOptions.mutations
     * .onError`: a mutation that declares its own `onError` *replaces* the default one, while
     * the cache callback always runs as well. Modules that already show a specific message
     * keep doing so; this is the floor, not the ceiling.
     */
    mutationCache: new MutationCache({
      onError: (error) => {
        reportError(error, 'mutation');
      },
    }),
    defaultOptions: {
      queries: {
        // The board screens (M0.F4) rely on realtime events, not polling, to stay fresh.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: 1,
      },
    },
  });
}

/**
 * Same `fetch` for both links: 401 anywhere means the session is gone (M0-F3 §5.3).
 */
const authAwareFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, { ...init, credentials: 'include' });
  if (response.status === 401 && typeof window !== 'undefined') {
    handleUnauthenticated(window.location.pathname, window.location.search);
  }
  return response;
};

export function createTrpcClient(url: string = TRPC_ENDPOINT) {
  return createTRPCClient<AppRouter>({
    links: [
      /*
       * Mutations do **not** batch, and that is what makes optimistic reconciliation
       * possible (M0-F5 §5.3): `x-mutation-id` is an HTTP header, so a batch of two
       * mutations in one request could only carry one id, and the events they produce
       * would be indistinguishable. Unbatched, each mutation carries its own id, the
       * `events` row echoes it, and the browser can tell "my own write coming back" from
       * "somebody else's" — the difference between no flicker and a flicker.
       *
       * Queries keep the batch link: they are the ones that arrive by the screenful.
       */
      splitLink({
        condition: (op) => op.type === 'mutation',
        true: httpLink({
          url,
          fetch: authAwareFetch,
          headers: (opts) => {
            const mutationId = (opts.op.context as { mutationId?: unknown }).mutationId;
            return {
              [CLIENT_HEADER]: CLIENT_HEADER_VALUE,
              ...(typeof mutationId === 'string' ? { 'x-mutation-id': mutationId } : {}),
            };
          },
        }),
        false: httpBatchLink({
          url,
          /**
           * A batched **query** travels as a GET with every input in the query string. When a
           * mutation invalidates a screenful of queries they all refetch in one batch, and the
           * URL passed nginx's (and Fastify's) limit — the browser logged `414 URI Too Long`
           * and the refetch silently produced nothing. tRPC splits a batch that would exceed
           * this into several requests instead.
           */
          maxURLLength: 1200,
          // The session cookie is HttpOnly and same-origin; `include` also covers the Vite dev
          // proxy, where the browser talks to :5173 and the api answers from :3100.
          fetch: authAwareFetch,
          headers: () => ({ [CLIENT_HEADER]: CLIENT_HEADER_VALUE }),
        }),
      }),
    ],
  });
}

/** True when a tRPC error is the api's "you are not signed in" answer. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED';
}

export type TrpcClient = ReturnType<typeof createTrpcClient>;
